import type { Team } from '@/lib/types'

/**
 * The per-team section switches from migrations/0044_team_features.sql.
 *
 * One list, read by three places that would otherwise drift apart: the sidebar
 * (components/app-shell.tsx), the route table (App.tsx) and the admin toggles
 * (pages/admin.tsx). A section switched off in the nav but still routed is an
 * empty page a stale link can reach; switched off in the route table but still
 * in the nav is a tab that bounces to the dashboard. Adding a feature should be
 * one edit here plus the column, not four edits in four files.
 *
 * ## What these are, and what they are not
 *
 * They hide a section. They are NOT a security boundary — the underlying tables
 * keep the policies they always had, and a member can still read their team's
 * rows through the API with the tab off. Turning Discussion off means "we do not
 * use this", not "this is now confidential".
 *
 * That is the opposite of how Reports and Coaching work, where the AUDIENCE is
 * restricted in SQL (0018, 0031) and the nav entry is a courtesy on top. For
 * those two the switch stacks: off hides it from everyone, on hands the decision
 * back to the existing policy. A flag can narrow who sees a tab; it can never
 * widen it.
 */
export interface TeamFeature {
  /** The boolean column on `teams`. */
  key: keyof Pick<
    Team,
    | 'topics_enabled'
    | 'schedule_enabled'
    | 'workshops_enabled'
    | 'discussion_enabled'
    | 'reports_enabled'
    | 'coaching_enabled'
    | 'games_enabled'
  >
  label: string
  /** What an admin is actually deciding, in one line. */
  description: string
  /**
   * Whether a team that has never been configured has this on. True for the
   * sections that existed before the switches did; false for anything added
   * since, which nobody has asked for yet.
   */
  defaultOn?: boolean
}

export const TEAM_FEATURES: TeamFeature[] = [
  {
    key: 'topics_enabled',
    label: 'Topics',
    // Worth spelling out: presenting a deck is reached FROM a topic, so this one
    // switch takes the whole deck-presenting path with it.
    description: 'The curriculum, and the decks presented from it.',
  },
  {
    key: 'schedule_enabled',
    label: 'Schedule',
    description: 'The dated plan of what the team will cover.',
  },
  {
    key: 'workshops_enabled',
    label: 'Workshops',
    description: 'Booked working sessions, their lessons and RSVPs.',
  },
  {
    key: 'discussion_enabled',
    label: 'Discussion',
    description: 'The team conversation.',
  },
  {
    key: 'reports_enabled',
    label: 'Reports',
    description: 'Monthly reports. The lead and DPL staff only, as before.',
  },
  {
    key: 'coaching_enabled',
    label: 'Coaching',
    description: '1:1 notes. DPL coaches only, as before.',
  },
  {
    key: 'games_enabled',
    label: 'Games',
    description: 'Short games the team plays together. Off until a team asks for it.',
    defaultOn: false,
  },
]

/**
 * Is a section on for this team?
 *
 * Defaults to TRUE when there is no team yet, or when the column is missing —
 * which happens for a moment during the session bootstrap, and would otherwise
 * make the whole sidebar flicker empty on every page load. Failing open is the
 * right default for a switch that only hides things; failing open on an
 * authorization check would not be, and this is not one.
 */
export function featureOn(team: Team | null | undefined, key: TeamFeature['key']): boolean {
  const defaultOn = TEAM_FEATURES.find((f) => f.key === key)?.defaultOn !== false
  // No team yet, or a column this build has never seen: fall back to the
  // feature's own default. Failing OPEN is right for the six that every team
  // already uses and wrong for one nobody has switched on, which would
  // otherwise flash a Games tab at every team for the length of a page load.
  if (!team) return defaultOn
  const value = team[key]
  return typeof value === 'boolean' ? value : defaultOn
}
